import { useEffect, useState } from "react";
import {
    Row,
    Col,
    Card,
    Button,
    notification,
    Form,
    Input,
    Select,
    Modal
} from "antd";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../../config";

const { Item } = Form;
const { Option } = Select;

function ReportElementAdd() {
    const { faqId } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [reportCategory, setReportCategory] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [newCategory, setNewCategory] = useState("");

    useEffect(() => {
        fetchReportElement();
        if (faqId) {
            setIsUpdateMode(true);
            fetchFaqDetails();
        }
    }, [faqId]);

    const fetchReportElement = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/config`);
            if (response.status === 200) {
                setReportCategory(response.data.result.report);
            } else {
                notification.info({
                    message: "Info",
                    description: response.data.message,
                    placement: "topRight",
                });
            }
        } catch (error) {
            console.error("API error:", error);
        }
    };

    const fetchFaqDetails = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/faq/read/${faqId}`);
            if (response.status === 200) {
                form.setFieldsValue(response.data.result);
            } else {
                notification.info({
                    message: "Info",
                    description: response.data.message,
                    placement: "topRight",
                });
            }
        } catch (error) {
            console.error("API error:", error);
            notification.info({
                message: "Info",
                description: error.response?.data?.message,
                placement: "topRight",
            });
        }
    };

    const handleAddCategory = async () => {
        if (!newCategory) {
            notification.warning({
                message: "Warning",
                description: "Please enter category name",
            });
            return;
        }

        try {
            const payload = {
                categories: [
                    {
                        key: newCategory.toLowerCase().replace(/\s+/g, "_"), // e.g. "Security Threat" -> "security_threat"
                        label: newCategory,
                        reasons: []
                    },
                ],
            };

            const response = await axios.patch(
                `${baseUrl}/api/report-management`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {

                const addPayload = {
                    categories: [
                        {
                            key: newCategory.toLowerCase().replace(/\s+/g, "_"), // e.g. "Security Threat" -> "security_threat"
                            category: newCategory,
                            reasons: []
                        },
                    ],
                };

                notification.success({
                    message: "Success",
                    description: "Category added successfully!",
                });

                // ✅ Update category list locally
                setReportCategory((prev) => [
                    ...prev,
                    addPayload.categories[0],
                ]);

                // ✅ Auto select newly added category
                form.setFieldsValue({
                    category: [
                        ...(form.getFieldValue("category") || []),
                        addPayload.categories[0].key,
                    ],
                });

                setNewCategory("");
                setModalVisible(false);
            }
        } catch (error) {
            notification.error({
                message: "Error",
                description: error.response?.data?.message || "Something went wrong",
            });
        }
    };


    const onFinish = async (values) => {
        try {
            const payload = {
                key: values.label.toLowerCase().replace(/\s+/g, "_"), // e.g. "Phishing Scam" -> "phishing_scam"
                label: values.label,
                description: values.description,
                assosiate_with: (values.assosiate_with || []).join(","), // convert array -> comma separated string
            };

            const response = await axios.patch(
                `${baseUrl}/api/report-management`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                notification.success({
                    message: "Success",
                    description: isUpdateMode
                        ? "Report element updated successfully!"
                        : "Report element added successfully!",
                    placement: "topRight",
                });
                form.resetFields();
                navigate("/report-element"); // ✅ change redirect if needed (maybe `/report-management` instead?)
            } else {
                notification.info({
                    message: "Info",
                    description: response.data.message,
                    placement: "topRight",
                });
            }
        } catch (error) {
            notification.error({
                message: "Error",
                description: error.response?.data?.message || "Something went wrong",
                placement: "topRight",
            });
        }
    };


    return (
        <div className="tabled">
            <Row gutter={[24, 0]}>
                <Col xs="24" xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title={isUpdateMode ? "Update Report Element" : "Add Report Element"}
                    >
                        <Form
                            style={{ padding: "20px" }}
                            form={form}
                            onFinish={onFinish}
                            layout="vertical"
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} lg={12}>
                                    <Item
                                        label="Label"
                                        name="label"
                                        rules={[{ required: true, message: "Please enter label." }]}
                                    >
                                        <Input placeholder="Enter label" />
                                    </Item>
                                </Col>
                                <Col xs={24} sm={12} lg={12}>
                                    <Item
                                        label="Description"
                                        name="description"
                                        rules={[{ required: true, message: "Please enter description." }]}
                                    >
                                        <Input placeholder="Enter description" />
                                    </Item>
                                </Col>

                                {/* ✅ Multi Select with Add Category Option */}
                                <Col xs={24} sm={12} lg={12}>
                                    <Item
                                        label="Category"
                                        name="assosiate_with"
                                        rules={[{ required: true, message: "Please select at least one option." }]}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select options"
                                            dropdownRender={(menu) => (
                                                <>
                                                    {menu}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            padding: 8,
                                                        }}
                                                    >
                                                        <Button
                                                            type="link"
                                                            onClick={() => setModalVisible(true)}
                                                        >
                                                            + Add Category
                                                        </Button>
                                                    </div>
                                                </>
                                            )}
                                        >
                                            {reportCategory.map((opt) => (
                                                <Option key={opt.key} value={opt.key}>
                                                    {opt.category}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={24} sm={12} lg={8}>
                                    <Item>
                                        <Button type="primary" htmlType="submit">
                                            {isUpdateMode ? "Update" : "Add"}
                                        </Button>
                                    </Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>
            </Row>

            {/* ✅ Add Category Modal */}
            <Modal
                title="Add New Category"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleAddCategory}
            >
                <Input
                    placeholder="Enter category name"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                />
            </Modal>
        </div>
    );
}

export default ReportElementAdd;
